package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.DailyRecordIds
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.auth.CallerCapabilities
import com.evoapps.evoshift.domain.usecase.auth.allows
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import kotlinx.datetime.plus

/** One day's net sales target. */
data class PlannedRevenueDay(val date: LocalDate, val target: Double)

/** The sales planned for a month, day by day ([fixedRevenueMonthly] comes on top when the workplace counts it). */
data class RevenuePlan(
    val days: List<PlannedRevenueDay>,
    val fixedRevenueMonthly: Double = 0.0
) {
    val total: Double get() = days.sumOf { it.target } + fixedRevenueMonthly
    /** Days with a target above zero. */
    val daysWithTarget: Int get() = days.count { it.target > 0 }

    /** Per weekday (Monday first): how many days, and their targets together. */
    val byWeekday: List<WeekdayPlan>
        get() = days.groupBy { it.date.dayOfWeek }.entries.sortedBy { it.key.ordinal }
            .map { (day, list) -> WeekdayPlan(day, list.size, list.sumOf { it.target }) }

    data class WeekdayPlan(val dayOfWeek: DayOfWeek, val days: Int, val total: Double)
}

/**
 * The revenue (sales) plan of a roster's month (SH-69): each day's target from its daily record
 * (where a manager may have changed it), otherwise from the workplace's weekday targets. Money:
 * null for those without VIEW_FINANCES, nothing is read for them.
 */
class GetRevenuePlanUseCase(
    private val workplaceRepository: WorkplaceRepository,
    private val financialRepository: FinancialRepository,
    private val caller: CallerCapabilities? = null
) {
    suspend operator fun invoke(workplaceId: String, rosterId: String, year: Int, month: Int): Result<RevenuePlan?> = runCatching {
        if (!caller.allows(workplaceId, Capability.VIEW_FINANCES)) return@runCatching null
        coroutineScope {
            val financials = async { workplaceRepository.getWorkplaceFinancials(workplaceId).getOrThrow() }
            val records = async { financialRepository.getDailyRecordsForRoster(rosterId).getOrDefault(emptyList()).associateBy { it.id } }
            val wf = financials.await()
            val byId = records.await()
            val days = generateSequence(LocalDate(year, month, 1)) { it.plus(DatePeriod(days = 1)) }
                .takeWhile { it.month.number == month }
                .map { date ->
                    val target = byId[DailyRecordIds.of(workplaceId, date.toString())]?.revenue?.planNetto
                        ?: wf.salesTargetFor(date.dayOfWeek) ?: 0.0
                    PlannedRevenueDay(date, target)
                }
                .toList()
            RevenuePlan(days, if (wf.includeFixedRevenueInDailyFinancialReport) wf.fixedRevenueMonthly else 0.0)
        }
    }
}
